import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { getHttpStatus, isApiError } from '@/api/errors';
import { categoryMetadata } from '@/utils/errors/apiErrors';
import { errorStack } from '@/utils/errors/errorDetailsText';
import { transactionPageUrl } from '@/utils/transactions/transactionUrl';
import type { JobResponse, SupportReportRequest } from '@/api/types';
import { reportError } from './api';

export function useReportErrorMutation() {
  return useMutation({
    mutationFn: reportError,
    onError: (error) => {
      console.warn('[support] error report failed', error);
    },
  });
}

export function notifyReportSent(reference?: string | null) {
  toast.success('Report sent', {
    description: reference
      ? `Our support team has been notified. Reference ${reference}.`
      : 'Our support team has been notified.',
  });
}

function baseSupportReport(message: string, transactionId?: string | null): SupportReportRequest {
  return {
    message,
    pageUrl: window.location.href,
    userAgent: navigator.userAgent,
    status: null,
    requestUrl: null,
    requestMethod: null,
    stack: null,
    componentStack: null,
    transactionId: transactionId ?? null,
    transactionUrl: transactionId ? transactionPageUrl(transactionId) : null,
  };
}

export function buildSupportReport(
  error: unknown,
  componentStack?: string | null
): SupportReportRequest {
  const api = isApiError(error) ? error : null;
  const axios = isAxiosError(error) ? error : null;

  const message =
    api?.detail ||
    (error instanceof Error ? error.message : undefined) ||
    'An unexpected error occurred.';

  const status = getHttpStatus(error);
  const requestUrl = api?.requestUrl ?? axios?.config?.url;
  const requestMethod = api?.requestMethod ?? axios?.config?.method?.toUpperCase();

  return {
    ...baseSupportReport(message, api?.transactionId),
    traceId: api?.traceId,
    status: status ?? null,
    requestUrl: requestUrl ?? null,
    requestMethod: requestMethod ?? null,
    stack: errorStack(error) ?? null,
    componentStack: componentStack ?? null,
  };
}

export function buildSupportReportFromJob(job: JobResponse): SupportReportRequest {
  const message =
    job.errorMessage?.trim() ||
    categoryMetadata(job.errorCategory)?.userMessage ||
    'Extraction failed.';
  return baseSupportReport(message, job.transactionId);
}
